import {isNil} from 'ramda';

import type {PricelistVersionRequestBody} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import {PricedItemBatchOutcomeUnknown} from '../types/PricedItemBatchOutcomeUnknown';
import {PricedItemGroupBuildFailure} from '../types/PricedItemGroupBuildFailure';
import {buildBatchOperations} from './buildBatchOperations';
import {catalogueErrorMessage, t} from './catalogueUi';
import {failedEntryIndex} from './failedEntryIndex';
import {isCodedEntryRefusal} from './isCodedEntryRefusal';
import {pricedItemGroupStore} from './pricedItemGroupStore';
import {trimSwitchRefusalMessage} from './trimSwitchRefusalMessage';

type BuiltBatch = Extract<Awaited<ReturnType<typeof buildBatchOperations>>, {outcome: 'built'}>;

const entryFailedMessage = (title: string) =>
  t(
    'entity.newVehicle.catalogue.itemGroup.operationFailed',
    '{change} could not be saved. Nothing in the group was saved.',
    {change: title}
  );

const entryRefusedMessage = (title: string, error: unknown) =>
  t(
    'entity.newVehicle.catalogue.groupEntryRefused',
    '{change} could not be saved: {reason} Nothing in the group was saved.',
    {change: title, reason: catalogueErrorMessage(error)}
  );

/** The batch without the operations and the rows of one entry, as after "Remove from group". */
const withoutEntry = (batch: BuiltBatch, key: string, entryIndex: number) => {
  const kept = batch.operations.flatMap((operation, index) =>
    batch.operationEntryIndexes[index] === entryIndex
      ? []
      : [{operation, entryIndex: batch.operationEntryIndexes[index]}]
  );
  return {
    ...batch,
    groups: batch.groups.filter((group) => group.key !== key),
    operations: kept.map(({operation}) => operation),
    operationEntryIndexes: kept.map((item) => item.entryIndex),
  };
};

export const rowsOf = (batch: BuiltBatch) => batch.groups.flatMap((group) => group.items);

/**
 * The entries under review after the removal of `key`. A removal takes out the entry of `key` and,
 * for a create, the entries that name the created item, or their parts that name it: an entry whose
 * key the removal touched is left out, or replaced by the entry of its key that the store keeps (a
 * matrix entry that lost the cells of a removed trim create). The other entries stay as reviewed.
 */
const reviewedAfterRemoval = (
  reviewed: PricedItemBatchEntry[],
  key: string,
  before: PricedItemBatchEntry[],
  after: PricedItemBatchEntry[]
) => {
  const takenKeys = new Set([
    key,
    ...before.filter((entry) => !after.includes(entry)).map((entry) => entry.key),
  ]);
  return reviewed.flatMap((entry) => {
    if (!takenKeys.has(entry.key)) {
      return [entry];
    }
    const replacement = after.find((left) => left.key === entry.key);
    return isNil(replacement) ? [] : [replacement];
  });
};

/**
 * The batch of an open group under review. "Remove from group" takes an entry out of the store and
 * out of what Confirm sends; a refusal with an operation index names its entry. When the removal
 * leaves a part of an entry in the group (the cells of stored trims of a matrix entry, after the
 * create of another trim of it is removed), the batch is built again from the entries left, so
 * Confirm sends that part too. Confirm waits for that build; a failed build rejects with
 * PricedItemGroupBuildFailure.
 */
export const reviewedGroupBatch = (
  yearId: string,
  entries: PricedItemBatchEntry[],
  built: BuiltBatch
) => {
  let batch = built;
  /** The entries `batch` was built from; its operation indexes point into these. */
  let batchEntries = entries;
  let reviewed = entries;
  const removeNow = async (key: string) => {
    const before = pricedItemGroupStore.get(yearId)?.entries ?? [];
    pricedItemGroupStore.remove(yearId, key);
    const after = pricedItemGroupStore.get(yearId)?.entries ?? [];
    const left = reviewedAfterRemoval(reviewed, key, before, after);
    const isRebuilt = left.some((entry) => !reviewed.includes(entry));
    if (isRebuilt) {
      const rebuilt = await buildBatchOperations(left);
      if (rebuilt.outcome === 'buildFailed') {
        throw new PricedItemGroupBuildFailure(left[rebuilt.entryIndex], rebuilt.error);
      }
      batch = rebuilt;
      batchEntries = left;
    } else {
      // The removal of a create also takes out the entries that name the created item. This
      // review sends none of them.
      reviewed
        .filter((entry) => !left.includes(entry))
        .forEach((taken) => {
          batch = withoutEntry(batch, taken.key, batchEntries.indexOf(taken));
        });
    }
    reviewed = left;
    return batch.operations.length === 0 ? undefined : {items: rowsOf(batch), groups: batch.groups};
  };
  /** The removals in the order they were made; a failed one fails the ones after it and Confirm. */
  let removals: Promise<unknown> = Promise.resolve();
  return {
    /** The operations Confirm sends, after the removals made so far. */
    operations: () => removals.then(() => batch.operations),
    /** The entries Confirm sends: all that were built, less the ones taken out of the group. */
    sentEntries: () => reviewed,
    /**
     * A coded refusal of one operation names its entry and the BE reason; a refusal of a trim switch
     * with a new item offered on it names the trim; another refusal of one operation names its entry.
     */
    failureMessage: (error: unknown) => {
      const entryIndex = failedEntryIndex(error, batch.operationEntryIndexes);
      if (!isNil(entryIndex) && isCodedEntryRefusal(error)) {
        return entryRefusedMessage(batchEntries[entryIndex].title, error);
      }
      return (
        trimSwitchRefusalMessage(error, batchEntries) ??
        (isNil(entryIndex) ? undefined : entryFailedMessage(batchEntries[entryIndex].title))
      );
    },
    removeEntry: (key: string) => {
      const removal = removals.then(() => removeNow(key));
      removals = removal;
      return removal;
    },
  };
};

/** A batch of the group whose outcome is unknown stays in it: Confirm reads it back first. */
export const keepUnknownOutcome = (
  error: unknown,
  sent: {
    yearId: string;
    sent: PricelistVersionRequestBody;
    review: {sentEntries: () => PricedItemBatchEntry[]};
  }
) => {
  if (error instanceof PricedItemBatchOutcomeUnknown) {
    pricedItemGroupStore.setUnknownOutcome(sent.yearId, {
      sent: sent.sent,
      entries: sent.review.sentEntries(),
    });
  }
};
